import PermissionButton from '../../permissions/PermissionButton';
import React from 'react';
import { Card, Table, Input, Button, Space, Select, message, Tag } from 'antd';
import { SearchOutlined, PlusOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { useFetchAllEntrepots, useDefaultEntrepot } from '../../hooks/entrepotHooks';
import { clearDefaultEntrepotCache } from '../../utils/defaultEntrepot';

const EntrepotList = ({ onSelectEntrepot, onCreateClick }) => {
  const user = useSelector(state => state.user);
  const isManager = !!user?.IsManager;
  const { entrepots, loading, error, refetch } = useFetchAllEntrepots();
  const {
    defaultCode,
    loading: loadingDefault,
    saving: savingDefault,
    setDefault,
  } = useDefaultEntrepot();
  const searchInput = React.useRef(null);
  const [searchText, setSearchText] = React.useState('');
  const [searchedColumn, setSearchedColumn] = React.useState('');

  const getColumnSearchProps = dataIndex => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={`Rechercher ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Rechercher
          </Button>
          <Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}>
            Réinitialiser
          </Button>
        </Space>
      </div>
    ),
    filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
    onFilter: (value, record) =>
      record[dataIndex] ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()) : '',
    onFilterDropdownVisibleChange: visible => {
      if (visible) {
        setTimeout(() => searchInput.current && searchInput.current.select());
      }
    },
    render: text => text
  });

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText('');
  };

  const handleDefaultChange = async (code_ent) => {
    try {
      await setDefault(code_ent);
      clearDefaultEntrepotCache();
      message.success('Entrepôt par défaut mis à jour');
      refetch();
    } catch (err) {
      message.error(err?.response?.data?.message || err.message || 'Erreur lors de la mise à jour');
    }
  };

  if (loading) return <p>Chargement...</p>;
  if (error) return <p>Erreur: {error}</p>;

  const columns = [
    {
      title: 'Code Entrepôt',
      dataIndex: 'code_ent',
      key: 'code_ent',
      ...getColumnSearchProps('code_ent'),
      render: (text, record) => (
        <Space>
          <span>{text}</span>
          {defaultCode === record.code_ent && <Tag color="blue">Par défaut</Tag>}
        </Space>
      ),
    },
    {
      title: 'Nom',
      dataIndex: 'name',
      key: 'name',
      ...getColumnSearchProps('name'),
    },
  ];

  const titleStyle = {
    fontSize: "24px",
    fontWeight: "bold",
    color: "#333",
    marginBottom: "16px",
    borderBottom: "2px solid #ccc",
    paddingBottom: "10px",
  };

  const tableCardStyle = {
    backgroundColor: "#fff",
    borderRadius: "8px",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
  };

  return (
    <div>
      <Card
        style={{ ...tableCardStyle, marginBottom: 16 }}
        title="Paramètres"
      >
        <Space direction="vertical" style={{ width: '100%' }} size="small">
          <div style={{ fontWeight: 500 }}>Entrepôt par défaut (application)</div>
          <Select
            placeholder="Choisir l'entrepôt par défaut"
            value={defaultCode}
            onChange={handleDefaultChange}
            loading={loadingDefault || savingDefault}
            disabled={!isManager || savingDefault}
            style={{ minWidth: 320, maxWidth: 480 }}
            showSearch
            optionFilterProp="label"
            options={(entrepots || []).map(ent => ({
              value: ent.code_ent,
              label: `${ent.code_ent} - ${ent.name}`,
            }))}
          />
          {!isManager && (
            <div style={{ color: '#888', fontSize: 12 }}>
              Seul un administrateur peut modifier l'entrepôt par défaut.
            </div>
          )}
        </Space>
      </Card>

      <Card 
        style={tableCardStyle}
        extra={
          onCreateClick && (
            <PermissionButton permissionModule="warehouses" permissionAction="create" type="primary" icon={<PlusOutlined />} onClick={onCreateClick}>
              Créer un Entrepôt
            </PermissionButton>
          )
        }
      >
        <h2 style={titleStyle}>Liste des Entrepôts</h2>
        <Table
          columns={columns}
          dataSource={entrepots}
          rowKey="code_ent"
          loading={loading}
          onRow={(record) => ({
            onClick: () => onSelectEntrepot(record.code_ent),
            style: { cursor: 'pointer' }
          })}
          pagination={{ pageSize: 10 }}
        />
      </Card>
    </div>
  );
};

export default EntrepotList;
